import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { styled } from "@mui/material/styles";
import { BasketItem } from "../../interfaces/core/BasketItem";
import Paper from "@mui/material/Paper";
import Snackbar, { SnackbarOrigin } from "@mui/material/Snackbar";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { Article } from "../../interfaces/Article";
import {
  addProductToBasket,
  getLiveArticleState,
} from "../../services/core-api";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Slide, { SlideProps } from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import Fade from "@mui/material/Fade";
import {
  Alert,
  Box,
  Grid,
  Skeleton,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  Popover,
  Typography,
  Button,
  Checkbox,
  useMediaQuery,
  MenuList,
  MenuItem,
  TextField,
  Stack,
  CardActions,
  IconButton,
  CardHeader,
} from "@mui/material";
import theme from "../../theme";
import ProductTabs from "./productTabs/ProductTabs";
import { addToCart } from "../../store/reducers/basket";
import { useSnackbar } from "../common/SnackbarContext";
import { LoadingButton } from "@mui/lab";
import { getUserPrice, getUserRetailPrice, getCurrentUser, getUserGroup, formatPriceWithCurrency, getCurrentUserCurrency, applyUserMargin } from "../../utils/pricingUtils";
import {
  canAddProductToBasket,
  canSeeProductPrice,
  resolveProductCategoryKey,
} from "../../utils/categoryPermissions";
import { useLocation } from "react-router-dom";
import {
  getWarehouseLabel,
  isKnownWarehouseCode,
} from "../../utils/warehouses";

interface State extends SnackbarOrigin {
  openNotification: boolean;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

const CustomIcon = styled(ContentCopyOutlinedIcon)({
  marginLeft: 10,
  marginRight: 0,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "transparent",
  color: "#000",
  boxShadow: "none",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
}));

const LocalProductItem = (props: any) => {
  const { article } = props;
  const location = useLocation();
  const hideInternalNumber = location.pathname.includes("/search-products");
  const isLoadingRef = React.useRef(false);
  const controllerRef = React.useRef<AbortController | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [erpProduct, setErpProduct] = React.useState<any | null>(null);
  const defaultImage = "./no-image.png";
  const productImage =
    article?.images?.[0]?.imageURL200 ||
    article?.images?.[0]?.imageURL800 ||
    article?.local_image ||
    article?.image ||
    defaultImage;
  const productImageLarge =
    article?.images?.[0]?.imageURL800 ||
    article?.images?.[0]?.imageURL200 ||
    article?.local_image ||
    article?.image ||
    defaultImage;
  const [openBasketNotification, setOpenBasketNotification] =
    React.useState(false);
  const [basketItem, setBasketItem] = React.useState<BasketItem | null>(null);
  const [value, setValue] = React.useState(0);
  const [isFavorite, setIsFavorite] = React.useState<Boolean | null>(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [price, setPrice] = React.useState(0);
  const [price_2, setPrice_2] = React.useState(0);
  const [isloadingStock, setIsLoadingStock] = React.useState(true);
  const [quantity, setQuantity] = React.useState(0);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const retailPrice = useSelector(
    (state: RootState) => state.priceReducer.retailPrice
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isRestrictedBrand = ["EXIDE", "HAGEN", "WOLF"].includes(
    (article?.mfrName || "").toString().toUpperCase()
  );
  const isFinanca5Tire = article?.source === "financa5_artikuj";
  const permissionCategory = resolveProductCategoryKey({
    productCategory: article?.category,
  });
  const isTireProduct =
    isFinanca5Tire ||
    permissionCategory === "tire" ||
    String(article?.category || "")
      .trim()
      .toUpperCase() === "GOMA";
  // F5 tire results carry CMSH on the article — always show that price on search-tyres
  const canSeePrice =
    !isRestrictedBrand &&
    (isFinanca5Tire || canSeeProductPrice(article));
  const canOrderCategory =
    !isRestrictedBrand &&
    (isFinanca5Tire || canAddProductToBasket(article));
  const tireDescription =
    (article?.description && String(article.description).trim()) || "";

  const priceFromCmsh = React.useCallback(
    (row?: { CMSH?: number | string | null; price?: number | string | null }) => {
      const raw = row?.CMSH ?? row?.price;
      if (raw === undefined || raw === null || raw === "") return 0;
      const cmsh = typeof raw === "number" ? raw : parseFloat(String(raw));
      if (!cmsh || cmsh <= 0 || Number.isNaN(cmsh)) return 0;
      return applyUserMargin(cmsh);
    },
    []
  );

  const applyArticlePrice = React.useCallback(() => {
    // Financa5 tires: use CMSH from ArtikujOData response
    const userPrice = isFinanca5Tire
      ? priceFromCmsh(article)
      : article?.CMSH != null
        ? getUserPrice(article)
        : Number(article?.price) || 0;
    if (userPrice > 0) {
      setPrice(userPrice);
      setPrice_2(userPrice);
      article.price = userPrice;
      article.offerPrice = userPrice;
      return userPrice;
    }
    return 0;
  }, [article, isFinanca5Tire, priceFromCmsh]);

  React.useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;

    if (article && !isLoadingRef.current) {
      // Show CMSH from Financa5 list response immediately when present
      applyArticlePrice();

      const productId = article.productId ? article.productId : article.id;
      const internalNumber = article.internalNumber;
      if (productId && internalNumber) {
        isLoadingRef.current = true;
        setIsLoadingStock(true);

        (async () => {
          try {
            if (controllerRef.current) {
            const internalNumberModified = internalNumber.replace(/\//g, "*");
            const erpArticle = await getLiveArticleState(
              internalNumberModified
            );
            if (erpArticle?.length) {
              setErpProduct(erpArticle);

              // F5 tires: keep CMSH (Artikuj first, then gjendje CMSH). Others: group price.
              const userPrice = isFinanca5Tire
                ? priceFromCmsh(article) || priceFromCmsh(erpArticle[0])
                : getUserPrice(erpArticle[0]);
              setPrice(userPrice);
              setPrice_2(userPrice);

              article.price = userPrice;
              article.offerPrice = userPrice;

              const total_quantity_raw = erpArticle.reduce(
                (acc: number, item: any) => acc + Number(item.GJENDJE || 0),
                0
              );
              const total_quantity = Math.max(0, total_quantity_raw);
              setQuantity(total_quantity);
              setValue(total_quantity > 0 ? 1 : 0);
            } else {
              // Keep Artikuj CMSH price when gjendje has no rows
              applyArticlePrice();
            }
            setIsLoadingStock(false);
          }
          } catch (error) {
            setIsLoadingStock(false);
            setQuantity(-1);
            setValue(0);
            article.total_quantity = -1;
            // Fall back to CMSH from Financa5 ArtikujOData instead of clearing price
            if (!applyArticlePrice()) {
              setPrice(0.0);
              setPrice_2(0.0);
              article.price = 0.0;
              article.offerPrice = 0.0;
            }
          }
        })();
      } else if (isFinanca5Tire) {
        setIsLoadingStock(false);
      }
    }
  }, [article, applyArticlePrice, isFinanca5Tire, priceFromCmsh]);

  const hideBasketNotification = () => {
    setStateBasketNotification({
      ...stateBasketNotification,
      openNotification: false,
    });
  };

  const [stateBasketNotification, setStateBasketNotification] =
    React.useState<State>({
      openNotification: false,
      vertical: "top",
      horizontal: "center",
    });

  // Add article to basket
  const addToBasket = async (
    article: Article,
    newStateBasketNotification: SnackbarOrigin
  ) => {
    setStateBasketNotification({
      ...newStateBasketNotification,
      openNotification: true,
    });
    setOpenBasketNotification(true);
    if (!canOrderCategory) {
      showSnackbar(
        "You are not allowed to order products from this category.",
        "error"
      );
      return;
    }

    const item: BasketItem = {
      productId: article.productId || article.id,
      internalNumber: article.internalNumber ?? "",
      articleNumber: article.articleNumber,
      mfrName: article.mfrName,
      dataSupplierId: article.dataSupplierId,
      image: "",
      description: tireDescription || article.description || "",
      price,
      quantity: value,
      totalPrice: parseFloat((price * value).toFixed(2)),
      available: true,
      currency: getCurrentUserCurrency(),
      permission_category: permissionCategory,
    };

    setBasketItem(item);
    const user = localStorage.getItem("user");
    if (user) {
      const parsedUser = JSON.parse(user);
      const user_id = parsedUser.id;

      try {
        await addProductToBasket(user_id, item);
        dispatch(addToCart(item));
      } catch (error) {}
    }
  };

  const { vertical, horizontal, openNotification } = stateBasketNotification;

  const handleChange = (event: any) => {
    const inputValue = event.target.value;
    
    if (inputValue === "") {
      setValue(1);
    } else {
      const numericValue = parseInt(inputValue);
      if (isNaN(numericValue)) {
        setValue(1);
      } else if (numericValue > 100) {
        setValue(200);
      } else if (numericValue < 1) {
        setValue(1);
      } else {
        setValue(numericValue);
      }
    }
  };

  const [state, setState] = React.useState<{
    open: boolean;
    Transition: React.ComponentType<
      TransitionProps & {
        children: React.ReactElement<any, any>;
      }
    >;
  }>({
    open: false,
    Transition: Fade,
  });

  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleClick =
    (
      Transition: React.ComponentType<
        TransitionProps & {
          children: React.ReactElement<any, any>;
        }
      >
    ) =>
    async () => {
      try {
        await navigator.clipboard.writeText(article.articleNumber);
        showSnackbar("Product article number copied!", "success");
      } catch (error) {
        showSnackbar("Failed to copy article number.", "error");
      }

      setState({
        open: false,
        Transition,
      });
    };

  const handleClose = () => {
    setState({
      ...state,
      open: false,
    });
  };

  return (
    <>
      {article ? (
        <>
          {isMobile ? (
            <>
              <Card sx={{ maxWidth: "100%", mb: 2 }}>
                <CardHeader
                  sx={{
                    backgroundColor: "#f1f1f1",
                    display: "flex",
                    alignItems: "center",
                    // Ensure the content area is flex container
                    "& .MuiCardHeader-content": {
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start", // Align items to the left
                      flexGrow: 1,
                      ml: 1, // Optional margin to space content from avatar
                    },
                    "& .MuiCardHeader-title": {
                      fontFamily: "Inter",
                      fontSize: "12px",
                      whiteSpace: "nowrap", // Prevent text wrapping
                    },
                    "& .MuiCardHeader-subheader": {
                      color: "red",
                      fontFamily: "Inter",
                      fontSize: "12px",
                      whiteSpace: "nowrap", // Prevent text wrapping
                    },
                    "& .MuiCardHeader-avatar": {
                      // Apply styles to the avatar slot
                      mr: 0,
                      minWidth: "40px",
                      "& .MuiButton-root": {
                        minWidth: "40px",
                        "&:focus, &:active": {
                          backgroundColor: "transparent",
                        },
                      },
                    },
                  }}
                  avatar={
                    <Button
                      sx={{
                        minWidth: "40px",
                        "&:focus, &:active": {
                          backgroundColor: "transparent",
                        },
                      }}
                      startIcon={<CustomIcon />}
                      onClick={handleClick(SlideTransition)}
                    ></Button>
                  }
                  action={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1, // Spacing between elements
                      }}
                    >
                      {isloadingStock ? (
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ width: "65px" }}
                        >
                          <LoadingButton
                            loading
                            variant="outlined"
                            sx={{
                              width: "65px",
                            }}
                          >
                            Submit
                          </LoadingButton>
                        </Stack>
                      ) : quantity >= 0 ? (
                        <Button
                          aria-label="box"
                          startIcon={
                            <img
                              style={{ width: "24px", height: "24px" }}
                              src="./available_status.svg"
                              alt="product status icon"
                            />
                          }
                          sx={{
                            minWidth: "40px",
                            ml: 2,
                            height: "33px",
                            padding: "4px 2px !important",
                            background: quantity < 1 ? "#F33" : (quantity === 1 ? "#FFA500" : "#0C0"),
                            "& .css-1d6wzja-MuiButton-startIcon": {
                              marginRight: "0px !important",
                              marginLeft: "0px !important",
                            },
                            borderRadius: 0,
                            ":hover": {
                              background: quantity < 1 ? "#F33" : (quantity === 1 ? "#FFA500" : "#0C0"),
                            },
                          }}
                          onMouseEnter={handlePopoverOpen}
                          onMouseLeave={handlePopoverClose}
                        ></Button>
                      ) : (
                        <></>
                      )}

                      {erpProduct && quantity !== -1 && (
                        <Popover
                          id="mouse-over-popover"
                          sx={{
                            pointerEvents: "none",
                          }}
                          open={open}
                          anchorEl={anchorEl}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                          }}
                          onClose={handlePopoverClose}
                          disableRestoreFocus
                        >
                          <MenuList
                            onMouseEnter={handlePopoverOpen}
                            onMouseLeave={handlePopoverClose}
                          >
                            {erpProduct
                            .filter((warehouse: any) =>
                              isKnownWarehouseCode((warehouse.KMAG || "").trim())
                            )
                            .map((warehouse: any, index: any) => (
                              <MenuItem key={index}>
                                <span
                                  style={{
                                    background:
                                      warehouse.GJENDJE < 1 ? "#F33" : (warehouse.GJENDJE === 1 ? "#FFA500" : "#0C0"),
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "15px",
                                  }}
                                ></span>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontFamily: "Inter",
                                    fontWeight: 500,
                                    display: "flex",
                                    alignItems: "center",
                                    ml: 1,
                                  }}
                                >
                                  {getWarehouseLabel(warehouse.KMAG)}
                                </Typography>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Popover>
                      )}
                      <TextField
                        disabled={quantity < 1}
                        variant="outlined"
                        size="small"
                        value={value}
                        onChange={handleChange}
                        sx={{
                          width: "45px",
                          "& .MuiInputBase-root": {
                            height: "35px",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "0",
                            width: "40px",
                            textAlign: "center",
                            height: "35px",
                          },
                          background: "#fff",
                        }}
                      />
                      <IconButton
                        aria-label="shopping cart"
                        disabled={quantity < 1 || !value || value > quantity || !canOrderCategory}
                        size="medium"
                        sx={{
                          background: theme.palette.secondary.main,
                          borderRadius: 0,
                          ":hover": {
                            backgroundColor: theme.palette.secondary.main,
                          },
                        }}
                        onClick={() =>
                          addToBasket(article, {
                            vertical: "top",
                            horizontal: "right",
                          })
                        }
                      >
                        <ShoppingCartOutlinedIcon
                          sx={{ color: "#fff", fontSize: "17px" }}
                        />
                      </IconButton>
                    </Box>
                  }
                  title={article.articleNumber}
                  subheader={
                    hideInternalNumber ? undefined : article.internalNumber
                  }
                />
                <CardMedia
                  component="img"
                  height="194"
                  image={productImageLarge}
                  alt="Paella dish"
                  sx={{ objectFit: "contain" }}
                />
                <CardContent>
                  <div>
                    {canSeePrice && (
                      retailPrice ? (
                        price_2 > 0 && (
                          <>
                            <Typography
                              variant="h6"
                              sx={{
                                float: "left",
                                fontSize: "15px",
                                fontFamily: "Inter",
                                fontWeight: "normal",
                                width: "max-content",
                              }}
                            >
                              Retail Price
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                float: "right",
                                color: theme.palette.secondary.main,
                                fontSize: "15px",
                                fontFamily: "Inter",
                                fontWeight: "bold",
                                width: "max-content",
                              }}
                            >
                              {formatPriceWithCurrency(price_2)}
                            </Typography>
                          </>
                        )
                      ) : (
                        <>
                          <Typography
                            variant="h6"
                            sx={{
                              float: "left",
                              fontSize: "15px",
                              fontFamily: "Inter",
                              fontWeight: "normal",
                              width: "max-content",
                            }}
                          >
                            Price
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              float: "right",
                              color: theme.palette.secondary.main,
                              fontSize: "15px",
                              fontFamily: "Inter",
                              fontWeight: "bold",
                              width: "max-content",
                            }}
                          >
                            {formatPriceWithCurrency(price)}
                          </Typography>
                        </>
                      )
                    )}
                  </div>
                  <div style={{ marginTop: "30px" }}>
                    <Typography
                      sx={{
                        textAlign: "left",
                        fontSize: "12px",
                        fontFamily: "Inter",
                        fontWeight: 500,
                      }}
                    >
                      {article.mfrName}
                    </Typography>
                  </div>

                  <div>
                    <Typography
                      sx={{
                        textAlign: "left",
                        fontSize: "12px",
                        fontFamily: "Inter",
                        fontWeight: "normal",
                      }}
                    >
                      Found via{" "}
                      <b style={{ fontWeight: 500 }}>Article number</b> (
                      {article.articleNumber})
                    </Typography>
                  </div>
                  {isTireProduct && tireDescription && (
                    <div>
                      <Typography
                        sx={{
                          textAlign: "left",
                          fontSize: "12px",
                          fontFamily: "Inter",
                          fontWeight: "normal",
                          mt: 0.5,
                        }}
                      >
                        {tireDescription}
                      </Typography>
                    </div>
                  )}
                </CardContent>
                <CardActions disableSpacing>
                  <ProductTabs
                    article={article}
                    qty={value}
                    isFavorite={isFavorite}
                    hideTecDocTabs={isTireProduct}
                  />
                </CardActions>
              </Card>
            </>
          ) : (
            <>
              <Grid container spacing={0}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    border: "4px solid #f1f1f1",
                    marginBottom: 0.5,
                    padding: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      background: "#f1f1f1",
                      pt: 0.5,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Item
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        sx={{
                          minWidth: "40px",
                          "&:focus, &:active": {
                            backgroundColor: "transparent",
                          },
                        }}
                        startIcon={<CustomIcon />}
                        onClick={handleClick(SlideTransition)}
                      ></Button>
                      <Typography
                        sx={{
                          fontSize: "15px",
                          fontFamily: "Inter",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {article.articleNumber}
                      </Typography>
                      {!hideInternalNumber && (
                        <Typography
                          sx={{
                            fontSize: "15px",
                            ml: 2,
                            color: "#F33",
                            fontFamily: "Inter",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {article.internalNumber}
                        </Typography>
                      )}
                    </Item>
                    <Item
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Tooltip title="Check to compare">
                        <Checkbox
                          disabled
                          inputProps={{ "aria-label": "controlled" }}
                          sx={{
                            textTransform: "capitalize",
                            color: theme.palette.primary.main,
                            "&.Mui-checked": {
                              color: theme.palette.primary.main,
                            },
                            display: "flex",
                            flexDirection: "row",
                          }}
                        />
                      </Tooltip>
                      {isloadingStock ? (
                        <Skeleton
                          variant="text"
                          sx={{ fontSize: "1rem", width: "60px", mr: 2 }}
                        />
                      ) : (
                        canSeePrice && (
                          retailPrice ? (
                            price_2 > 0 && (
                              <>
                                <Box
                                  sx={{
                                    display: "flex",
                                    mr: 2,
                                    flexDirection: "column", // Ensures vertical layout
                                    alignItems: "flex-start", // Align text to the start (left-aligned)
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "15px",
                                      fontFamily: "Inter",
                                      fontWeight: "normal",
                                    }}
                                  >
                                    {formatPriceWithCurrency(price_2)}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      fontFamily: "Inter",
                                      fontWeight: "normal",
                                    }}
                                  >
                                    Retail price
                                  </Typography>
                                </Box>
                              </>
                            )
                          ) : (
                            price > 0 && (
                              <Typography
                                sx={{
                                  fontSize: "15px",
                                  fontFamily: "Inter",
                                  fontWeight: "normal",
                                  display: "flex",
                                  alignItems: "center",
                                  mr: 2,
                                }}
                              >
                                {formatPriceWithCurrency(price)}
                              </Typography>
                            )
                          )
                        )
                      )}
                      {price &&
                        (isloadingStock ? (
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{ width: "65px" }}
                          >
                            <LoadingButton
                              loading
                              variant="outlined"
                              sx={{
                                width: "65px",
                              }}
                            >
                              Submit
                            </LoadingButton>
                          </Stack>
                        ) : (
                          <Button
                            startIcon={
                              <img
                                style={{ width: "27px", height: "27px" }}
                                src="./available_status.svg"
                                alt="product status icon"
                              />
                            }
                            sx={{
                              padding: "4px 2px !important",
                              background: quantity < 1 ? "#F33" : (quantity === 1 ? "#FFA500" : "#0C0"),
                              "& .css-1d6wzja-MuiButton-startIcon": {
                                marginRight: "0px !important",
                                marginLeft: "0px !important",
                              },
                              borderRadius: 0,
                              ":hover": {
                                background: quantity < 1 ? "#F33" : (quantity === 1 ? "#FFA500" : "#0C0"),
                              },
                            }}
                            onMouseEnter={handlePopoverOpen}
                            onMouseLeave={handlePopoverClose}
                          ></Button>
                        ))}

                      {erpProduct && quantity !== -1 && (
                        <Popover
                          id="mouse-over-popover"
                          sx={{
                            pointerEvents: "none",
                          }}
                          open={open}
                          anchorEl={anchorEl}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                          }}
                          onClose={handlePopoverClose}
                          disableRestoreFocus
                        >
                          <MenuList
                            onMouseEnter={handlePopoverOpen}
                            onMouseLeave={handlePopoverClose}
                          >
                            {erpProduct
                            .filter((warehouse: any) =>
                              isKnownWarehouseCode((warehouse.KMAG || "").trim())
                            )
                            .map((warehouse: any, index: any) => (
                              <MenuItem key={index}>
                                <span
                                  style={{
                                    background:
                                      warehouse.GJENDJE < 1 ? "#F33" : (warehouse.GJENDJE === 1 ? "#FFA500" : "#0C0"),
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "15px",
                                  }}
                                ></span>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontFamily: "Inter",
                                    fontWeight: 500,
                                    display: "flex",
                                    alignItems: "center",
                                    ml: 1,
                                  }}
                                >
                                  {getWarehouseLabel(warehouse.KMAG)}
                                </Typography>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Popover>
                      )}
                      <TextField
                        id="standard-number"
                        disabled={!quantity}
                        size="medium"
                        variant="standard"
                        value={value}
                        onChange={handleChange}
                        sx={{
                          width: "50px",
                          background: "#fff",
                          marginLeft: 2,
                          marginRight: 2,
                          "& .MuiInput-underline:before": {
                            borderBottom: "none", // Remove the bottom border before hover
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottom: "none", // Remove the bottom border on hover
                            },
                          "& .MuiInput-underline:after": {
                            borderBottom: "none", // Remove the bottom border after clicking
                          },
                        }}
                        InputProps={{
                          sx: {
                            textAlign: "center",
                            border: "1px solid #d4d4d4",
                            height: "36px",
                            fontSize: "13px",
                            fontFamily: "Inter",
                            paddingLeft: ".9rem",
                            width: "100%",
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                              {
                                borderBottom: "none", // Remove the bottom border on hover
                              },
                            "& .MuiInput-underline:after": {
                              borderBottom: "none", // Remove the bottom border after clicking
                            },
                          },
                        }}
                      />
                       <Button
                         disabled={!value || value > quantity || !canOrderCategory}
                        aria-label="basket"
                        size="medium"
                        sx={{
                          background: theme.palette.secondary.main,
                          borderRadius: 0,
                          ":hover": {
                            backgroundColor: "#df0219",
                          },
                        }}
                        onClick={() =>
                          addToBasket(article, {
                            vertical: "top",
                            horizontal: "right",
                          })
                        }
                      >
                        <ShoppingCartOutlinedIcon
                          sx={{ color: "#fff" }}
                          fontSize="medium"
                        />
                      </Button>
                    </Item>
                  </Box>
                  <Box sx={{ display: "flex" }}>
                    <Item
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        style={{ width: "100px" }}
                        src={productImage}
                        alt="Product"
                      />
                    </Item>
                    <Item
                      sx={{
                        flex: 11,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                      }}
                    >
                      <div>
                        <Typography
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Inter",
                            fontWeight: 500,
                          }}
                        >
                          {article.mfrName}
                        </Typography>
                      </div>
                      <div>
                        <Typography
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Inter",
                            fontWeight: "normal",
                          }}
                        >
                          Found via{" "}
                          <b style={{ fontWeight: 500 }}>Article number</b> (
                          {article.articleNumber})
                        </Typography>
                      </div>
                      <div>
                        <Typography
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Inter",
                            fontWeight: "normal",
                          }}
                        >
                          {article.width && (
                            <>
                              <b style={{ fontWeight: 500 }}>Width</b>:{" "}
                              {article.width};{" "}
                            </>
                          )}
                          {article.height && (
                            <>
                              <b style={{ fontWeight: 500 }}>Height</b>:{" "}
                              {article.height};{" "}
                            </>
                          )}
                          {article.dimensions && (
                            <>
                              <b style={{ fontWeight: 500 }}>Dimensions</b>:{" "}
                              {article.dimensions};{" "}
                            </>
                          )}
                        </Typography>
                      </div>
                      {isTireProduct && tireDescription && (
                        <div>
                          <Typography
                            sx={{
                              textAlign: "left",
                              fontSize: "13px",
                              fontFamily: "Inter",
                              fontWeight: "normal",
                              mt: 0.5,
                            }}
                          >
                            {tireDescription}
                          </Typography>
                        </div>
                      )}
                    </Item>
                  </Box>
                  <Box sx={{ display: "flex" }}>
                    <ProductTabs
                      article={article}
                      qty={value}
                      hideTecDocTabs={isTireProduct}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Snackbar
                open={state.open}
                onClose={handleClose}
                TransitionComponent={state.Transition}
                key={state.Transition.name}
                autoHideDuration={1200}
              >
                <Alert
                  onClose={handleClose}
                  severity="info"
                  sx={{ width: "100%" }}
                >
                  {article.articleNumber} copied!
                </Alert>
              </Snackbar>
              <Snackbar
                key={vertical + horizontal}
                anchorOrigin={{ vertical, horizontal }}
                open={openNotification}
                autoHideDuration={1500}
                onClose={hideBasketNotification}
              >
                <Alert
                  onClose={hideBasketNotification}
                  severity="success"
                  sx={{
                    bgcolor: "#fff",
                    width: "100%",
                    boxShadow: "4px 5px 5px #cfcfcf",
                  }}
                >
                  <Card sx={{ display: "flex", boxShadow: "none" }}>
                    <CardMedia
                      component="img"
                      sx={{ width: 120 }}
                      image={productImageLarge}
                      alt="Live from space album cover"
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <CardContent sx={{ flex: "1 0 auto" }}>
                        <Typography
                          component="div"
                          variant="subtitle1"
                          sx={{
                            color: theme.palette.text.primary,
                            fontSize: "12px",
                            fontFamily: "Inter",
                            fontWeight: 500,
                          }}
                        >
                          {article.manufacturer_name}{" "}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          component="div"
                          sx={{
                            textAlign: "left",
                            color: theme.palette.text.primary,
                            fontSize: "12px",
                            fontFamily: "Inter",
                            fontWeight: 500,
                          }}
                        >
                          Quantity x {value}
                        </Typography>
                        {canSeePrice && (
                          <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            component="div"
                            sx={{
                              textAlign: "left",
                              color: theme.palette.text.primary,
                              fontSize: "12px",
                              fontFamily: "Inter",
                              fontWeight: 500,
                            }}
                          >
                            Price: {formatPriceWithCurrency(article.price || 0)}
                          </Typography>
                        )}
                      </CardContent>
                    </Box>
                  </Card>
                </Alert>
              </Snackbar>
            </>
          )}
        </>
      ) : (
        <Grid container spacing={0}>
          <Grid
            item
            xs={12}
            sx={{
              border: "4px solid #f1f1f1",
              marginBottom: 0.5,
              padding: 0.5,
            }}
          >
            <Box
              sx={{
                background: "#f1f1f1",
                pt: 0.5,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "80px", height: "30px" }}
                />
              </Item>
              <Item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Check to compare">
                  <Skeleton
                    animation="wave"
                    sx={{ width: "30px", height: "30px", mr: 1 }}
                  />
                </Tooltip>
                <Skeleton
                  animation="wave"
                  sx={{ width: "80px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px" }}
                />
              </Item>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Item
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Skeleton
                  animation="wave"
                  sx={{ width: "5rem", height: "7rem" }}
                />
              </Item>
              <Item
                sx={{
                  flex: 11,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                <div>
                  <Skeleton animation="wave" sx={{ width: "40px" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "50px" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "40rem" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "30rem" }} />
                  <Skeleton animation="wave" sx={{ width: "35rem" }} />
                  <Skeleton animation="wave" sx={{ width: "30rem" }} />
                </div>
              </Item>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              border: "4px solid #f1f1f1",
              marginBottom: 0.5,
              padding: 0.5,
            }}
          >
            <Box
              sx={{
                background: "#f1f1f1",
                pt: 0.5,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "80px", height: "30px" }}
                />
              </Item>
              <Item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Check to compare">
                  <Skeleton
                    animation="wave"
                    sx={{ width: "30px", height: "30px", mr: 1 }}
                  />
                </Tooltip>
                <Skeleton
                  animation="wave"
                  sx={{ width: "80px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px" }}
                />
              </Item>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Item
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Skeleton
                  animation="wave"
                  sx={{ width: "5rem", height: "7rem" }}
                />
              </Item>
              <Item
                sx={{
                  flex: 11,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                <div>
                  <Skeleton animation="wave" sx={{ width: "40px" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "50px" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "40rem" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "30rem" }} />
                  <Skeleton animation="wave" sx={{ width: "35rem" }} />
                  <Skeleton animation="wave" sx={{ width: "30rem" }} />
                </div>
              </Item>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              border: "4px solid #f1f1f1",
              marginBottom: 0.5,
              padding: 0.5,
            }}
          >
            <Box
              sx={{
                background: "#f1f1f1",
                pt: 0.5,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "80px", height: "30px" }}
                />
              </Item>
              <Item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Check to compare">
                  <Skeleton
                    animation="wave"
                    sx={{ width: "30px", height: "30px", mr: 1 }}
                  />
                </Tooltip>
                <Skeleton
                  animation="wave"
                  sx={{ width: "80px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px", mr: 1 }}
                />
                <Skeleton
                  animation="wave"
                  sx={{ width: "30px", height: "30px" }}
                />
              </Item>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Item
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Skeleton
                  animation="wave"
                  sx={{ width: "5rem", height: "7rem" }}
                />
              </Item>
              <Item
                sx={{
                  flex: 11,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                <div>
                  <Skeleton animation="wave" sx={{ width: "40px" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "50px" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "40rem" }} />
                </div>
                <div>
                  <Skeleton animation="wave" sx={{ width: "30rem" }} />
                  <Skeleton animation="wave" sx={{ width: "35rem" }} />
                  <Skeleton animation="wave" sx={{ width: "30rem" }} />
                </div>
              </Item>
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};
export default LocalProductItem;
